import { Canvas } from '@react-three/fiber';
import { Suspense, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import ShittyMapWorld from './ShittyMapWorld';
import ShittyMapControls from './ShittyMapControls';
import ShittyMapFilterBar from './ShittyMapFilterBar';
import ShittyMapDetailPanel from './ShittyMapDetailPanel';
import ShittyMapLoadingOverlay from './ShittyMapLoadingOverlay';
import { useCitiesStore, useCitiesResolver } from '@/hooks/useCitiesStore';
import { useShittyMapStore } from '@/hooks/useShittyMapStore';

function ZoomControls() {
  const { cameraPosition, setFocusTarget } = useShittyMapStore();

  const handleZoom = useCallback((delta) => {
    const currentZoom = cameraPosition?.zoom || 0.4;
    const newZoom = Math.max(0.2, Math.min(4, currentZoom * (1 + delta)));
    setFocusTarget({
      x: cameraPosition?.x || 0,
      y: cameraPosition?.y || 0,
      zoom: newZoom,
      duration: 300,
    });
  }, [cameraPosition, setFocusTarget]);

  const handleReset = useCallback(() => {
    setFocusTarget({
      x: 0,
      y: 0,
      zoom: 2,
      duration: 800,
    });
  }, [setFocusTarget]);

  const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const modKey = isMac ? '⌘' : 'Ctrl';

  return (
    <div className="absolute bottom-6 right-6 z-20 flex flex-col gap-2 items-end pointer-events-none">
      <div className="text-xs text-gray-500 bg-gray-900/80 px-2 py-1 rounded mb-1 backdrop-blur-sm">
        {modKey} + scroll to zoom
      </div>
      <button
        onClick={() => handleZoom(0.5)}
        className="w-10 h-10 bg-gray-900/90 hover:bg-gray-800 border border-gray-700/50 hover:border-primary-500/50 rounded-lg flex items-center justify-center text-white text-xl font-light transition-all shadow-lg backdrop-blur-sm pointer-events-auto"
        aria-label="Zoom in"
      >
        +
      </button>
      <button
        onClick={() => handleZoom(-0.35)}
        className="w-10 h-10 bg-gray-900/90 hover:bg-gray-800 border border-gray-700/50 hover:border-primary-500/50 rounded-lg flex items-center justify-center text-white text-xl font-light transition-all shadow-lg backdrop-blur-sm pointer-events-auto"
        aria-label="Zoom out"
      >
        −
      </button>
      <button
        onClick={handleReset}
        className="w-10 h-10 bg-gray-900/90 hover:bg-gray-800 border border-gray-700/50 hover:border-primary-500/50 rounded-lg flex items-center justify-center text-gray-400 hover:text-white transition-all shadow-lg backdrop-blur-sm mt-1 pointer-events-auto"
        aria-label="Reset view"
        title="Reset view"
      >
        <svg width={16} height={16} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
        </svg>
      </button>
    </div>
  );
}

export default function ShittyMapCanvas() {
  useCitiesResolver();
  const { loading: citiesLoading } = useCitiesStore();

  const showLoading = citiesLoading;

  return (
    <div className="relative w-full" style={{ height: 'calc(100vh - 180px)', minHeight: '600px' }}>
      <Canvas
        orthographic
        camera={{
          zoom: 2,
          position: [0, 0, 1000],
          near: 0.1,
          far: 2000,
        }}
        style={{
          background: 'linear-gradient(180deg, #0d0d14 0%, #111827 50%, #1a1a2e 100%)',
          touchAction: 'none',
        }}
        gl={{ antialias: true, alpha: false }}
        dpr={[1, 2]}
      >
        <Suspense fallback={null}>
          <ShittyMapWorld />
          <ShittyMapControls />
        </Suspense>
        <ambientLight intensity={1} />
      </Canvas>

      <ShittyMapFilterBar />
      <ShittyMapDetailPanel />
      <ZoomControls />
      <AnimatePresence>
        {showLoading && <ShittyMapLoadingOverlay />}
      </AnimatePresence>
    </div>
  );
}
